import { useEffect, useState } from "react";
import { Box, Stepper, Step, StepLabel, Typography, Button, CircularProgress } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useOnboardingStore } from "../store/onboardingSlice";
import { cancelOnboarding } from "../api/auth";
import BusinessBasicsStep from "../components/onboarding/BusinessBasicsStep";
import CategoriesStep from "../components/onboarding/CategoriesStep";
import ServicesStep from "../components/onboarding/ServicesStep";
import StaffStep from "../components/onboarding/StaffStep";
import CalendarStep from "../components/onboarding/CalendarStep";
import LaunchChecklistStep from "../components/onboarding/LaunchChecklistStep";
import {
  getPostAuthDestination,
  syncMarketingPlanInterestFromSearchParams,
} from "../lib/billingSelection";
import { logoutAndResetPortalSession } from "../lib/portalSession";
import { trackMarketingPlanInterestCaptured, trackOnboardingStepView } from "../lib/analytics";

const STEPS = [
  "Business basics",
  "Categories",
  "Your services",
  "Your staff",
  "Calendar",
  "Launch",
];

const STEP_ANALYTICS = {
  1: "business_basics",
  2: "service_categories",
  3: "services",
  4: "staff",
  5: "calendar_connection",
  6: "launch_checklist",
} as const;

export default function OnboardingWizard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { onboardingCompleted, businessId, accountStatus, initializing } = useAuth();
  const { currentStep, reset, setCurrentStep } = useOnboardingStore();
  const [cancelling, setCancelling] = useState(false);

  const handleCancel = async () => {
    setCancelling(true);
    try {
      if (businessId) {
        await cancelOnboarding();
      }
    } catch {
      console.warn("Unable to cancel onboarding before resetting portal session");
    } finally {
      reset();
      await logoutAndResetPortalSession(navigate);
    }
  };

  useEffect(() => {
    const marketingPlanInterest = syncMarketingPlanInterestFromSearchParams(searchParams);
    if (marketingPlanInterest) {
      trackMarketingPlanInterestCaptured(marketingPlanInterest, "/onboarding");
    }
  }, [searchParams]);

  useEffect(() => {
    if (initializing) return;
    // Redirect if wizard already completed, OR if this is an existing 'active' account
    // that predates the new onboarding flow
    if (businessId && (onboardingCompleted || accountStatus === "active")) {
      navigate(getPostAuthDestination(), { replace: true });
    }
  }, [businessId, onboardingCompleted, accountStatus, initializing, navigate]);

  useEffect(() => {
    const requestedStep = Number(searchParams.get("step"));
    if (requestedStep >= 1 && requestedStep <= 6) {
      setCurrentStep(requestedStep as 1 | 2 | 3 | 4 | 5 | 6);
    }
  }, [searchParams, setCurrentStep]);

  useEffect(() => {
    trackOnboardingStepView(currentStep, STEP_ANALYTICS[currentStep], true);
  }, [currentStep]);

  const renderStep = () => {
    switch (currentStep) {
      case 1: return <BusinessBasicsStep />;
      case 2: return <CategoriesStep />;
      case 3: return <ServicesStep />;
      case 4: return <StaffStep />;
      case 5: return <CalendarStep />;
      case 6: return <LaunchChecklistStep />;
      default: return null;
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "background.default",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        component="header"
        sx={{
          px: { xs: 3, md: 6 },
          py: 2,
          borderBottom: 1,
          borderColor: "divider",
          backgroundColor: "background.paper",
          display: "flex",
          alignItems: "center",
          gap: 3,
        }}
      >
        <img src="/media/logo.png" alt="Setin" style={{ height: 32 }} />
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
          Set up your account
        </Typography>
        <Button
          variant="text"
          size="small"
          disabled={cancelling}
          onClick={handleCancel}
          sx={{ color: "text.secondary", fontSize: "0.8rem" }}
        >
          {cancelling ? <CircularProgress size={14} sx={{ mr: 1 }} /> : null}
          Use a different account
        </Button>
      </Box>

      <Box sx={{ flex: 1, display: "flex", flexDirection: { xs: "column", md: "row" } }}>
        <Box
          sx={{
            width: { xs: "100%", md: 260 },
            px: { xs: 3, md: 4 },
            py: 4,
            borderRight: { md: 1 },
            borderBottom: { xs: 1, md: 0 },
            borderColor: "divider",
            backgroundColor: "background.paper",
          }}
        >
          <Stepper activeStep={currentStep - 1} orientation="vertical">
            {STEPS.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        <Box
          sx={{
            flex: 1,
            px: { xs: 3, sm: 6 },
            py: 5,
            maxWidth: 640,
            mx: "auto",
            width: "100%",
          }}
        >
          {renderStep()}
        </Box>
      </Box>
    </Box>
  );
}
